import backgroundProfile from "@images/backgroundProfile.png"
import { word } from "@modules/core/utils/i18n"
import { usePrograms } from "@modules/programs/hooks/usePrograms"
import { ItemProgram } from "@modules/programs/programTypes"
import { HuProgramMiniCards } from "@modules/ui/components/cards/huProgramMiniCard"
import { HUBackgroundImageView } from "@modules/ui/components/huBackgroundImageView"
import { HUText } from "@modules/ui/components/huText"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { Carousel } from "primereact/carousel"
import { useCallback } from "react"
import styled from "styled-components"

export const ProfilePrograms: React.FC<{ openProgramDisabled: boolean }> = ({ openProgramDisabled }) => {
  const { width } = useWindowSize()
  const { result: userPrograms, loading: loadingUserPrograms } = usePrograms({ programFilter: "OWNED" })
  const { result: subscribedPrograms, loading: loadingSubscribedPrograms } = usePrograms({
    programFilter: "SUBSCRIBED",
  })
  const { result: sharedCoachPrograms, loading: loadingSharedCoachPrograms } = usePrograms({
    programFilter: "SHARED_COACH",
  })

  const programCardCarrouselTemplate = useCallback(
    (program: ItemProgram, hideTitle: boolean) => {
      if (userPrograms.findIndex((userProgram) => userProgram.id === program.id) >= 0) {
        return (
          <>
            {!hideTitle && (
              <div className="mb-1 w-12rem">
                <HUText fontStyle="LS" className="text-align-start">
                  {word("program.label.author")}
                </HUText>
              </div>
            )}
            <HuProgramMiniCards item={program} disabled={openProgramDisabled} />
          </>
        )
      }
      if (sharedCoachPrograms.findIndex((sharedCoachProgram) => sharedCoachProgram.id === program.id) >= 0) {
        const index = sharedCoachPrograms.findIndex((p) => p.id === program.id)
        const shouldDisplayFirstItemLine = index === 0 && userPrograms.length > 0 && !hideTitle
        return (
          <div className={"flex flex-column align-items-center " + (shouldDisplayFirstItemLine ? "first-item" : "")}>
            {!hideTitle && (
              <div className="mb-1 w-12rem">
                <HUText fontStyle="LS">{word("program.sharedCoach.title")}</HUText>
              </div>
            )}

            <HuProgramMiniCards item={program} disabled={openProgramDisabled} />
          </div>
        )
      }

      return <HuProgramMiniCards item={program} disabled={openProgramDisabled} />
    },
    [userPrograms, sharedCoachPrograms],
  )

  if (loadingUserPrograms || loadingSubscribedPrograms || loadingSharedCoachPrograms) return
  if (userPrograms.length === 0 && subscribedPrograms.length === 0 && sharedCoachPrograms.length === 0)
    return (
      <HUBackgroundImageView
        image={{ path: backgroundProfile }}
        width="100%"
        height="100%"
        className="card bg-neutralColor-50 hidden lg:block"
        style={{ backgroundRepeat: "repeat", backgroundSize: "auto" }}
      />
    )

  return (
    <div className="gap-8">
      {(userPrograms.length > 0 || sharedCoachPrograms.length > 0) && (
        <div className="mb-6">
          <HUText fontStyle="TM">{word("publicProfile.userPersonalPrograms")}</HUText>
          <div className="flex flex-wrap gap-2 mt-2">
            <StyledCarousel
              className="w-full"
              showIndicators={false}
              showNavigators={true}
              numVisible={width >= 1400 ? 2 : 1}
              value={userPrograms.concat(sharedCoachPrograms) as ItemProgram[]}
              itemTemplate={(item: ItemProgram) => programCardCarrouselTemplate(item, false)}
            />
          </div>
        </div>
      )}
      {subscribedPrograms.length > 0 && (
        <div>
          <HUText fontStyle="TM">{word("publicProfile.followedPrograms")}</HUText>
          <div className="flex flex-wrap gap-2 mt-2">
            <StyledCarousel
              className="w-full"
              showIndicators={false}
              showNavigators={true}
              numVisible={width >= 1400 ? 2 : 1}
              value={subscribedPrograms as ItemProgram[]}
              itemTemplate={(item: ItemProgram) => programCardCarrouselTemplate(item, true)}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const StyledCarousel = styled(Carousel)`
  .p-carousel-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    &:has(.first-item) {
      border-left: 1px solid ${({ theme }) => theme.neutralColor300};
    }
  }
`
